import {
  ApiChatRequest,
  ApiChatEventResponse,
  ChatMessage,
  ChatRequest,
  ChatResponse,
  ApiChatContentResponse,
  ApiLiveChatContentResponse,
  ApiLiveChatEventResponse,
  LiveChatDataSchema,
} from './types'
// import mockResponse from '../../mock-data/chat/chat-reponse.json'
import { ENDPOINTS } from '@/util'
import { getMagic8BallAnswer } from './magic8ball'

// Session storage helpers
export const CHAT_MESSAGES_STORAGE_KEY = 'chat-messages'
export const CHAT_HANDLER_ID_STORAGE_KEY = 'chat-handler-id'
export const LIVE_CHAT_SESSION_STORAGE_KEY = 'live-chat-session'
const USE_MOCK_RESPONSE = false
const USE_MOCK_DATA = false

// LiveChat session data stored in sessionStorage
interface LiveChatSessionData {
  requestId: string
  taskId: number
  appKey: string
  contentId: string
  createdAt: number
}

const getLiveChatSession = (contentId: string): LiveChatSessionData | null => {
  try {
    const stored = sessionStorage.getItem(LIVE_CHAT_SESSION_STORAGE_KEY)
    if (!stored) return null
    const session = JSON.parse(stored) as LiveChatSessionData
    // Only return if contentId matches (same chat component)
    if (session.contentId === contentId) {
      return session
    }
    return null
  } catch {
    return null
  }
}

const saveLiveChatSession = (session: LiveChatSessionData): void => {
  try {
    sessionStorage.setItem(LIVE_CHAT_SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch (error) {
    console.warn('Failed to save live chat session:', error)
  }
}

const clearLiveChatSession = (): void => {
  try {
    sessionStorage.removeItem(LIVE_CHAT_SESSION_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear live chat session:', error)
  }
}

const loadMessagesFromStorage = (): ChatMessage[] => {
  try {
    const stored = sessionStorage.getItem(CHAT_MESSAGES_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const saveMessagesToStorage = (messages: ChatMessage[]) => {
  try {
    sessionStorage.setItem(CHAT_MESSAGES_STORAGE_KEY, JSON.stringify(messages))
  } catch (error) {
    console.warn('Failed to save messages to session storage:', error)
  }
}

// Mock API functions
const fetchChatHistory = async (): Promise<ChatMessage[]> => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 0))

  // Load from session storage or return empty array
  return loadMessagesFromStorage()
}

const sendChatMessage = async (
  handlerId: string,
  appKey?: string,
  projectId?: number,
  request?: ChatRequest
): Promise<ChatResponse> => {
  if (!appKey || !projectId || !request) {
    throw new Error('Missing required parameters')
  }

  if (USE_MOCK_RESPONSE) {
    await new Promise((resolve) => setTimeout(resolve, 3000))
    return {
      message: getMagic8BallAnswer(),
      id: `assistant-${Date.now()}`,
      timestamp: new Date().toISOString(),
    }
  }

  const apiRequest: ApiChatRequest = {
    app_id: appKey,
    content_id: handlerId,
    project_id: projectId,
    filters: [{}],
    payload: {
      message: request.message,
      history: request.history,
    },
  }

  const token = JSON.parse(sessionStorage.getItem('token') || '')

  try {
    const response = await fetch(`${ENDPOINTS.CC_DOTNET}/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(apiRequest),
    })

    if (!response.ok) {
      throw new Error('Failed to send chat message')
    }

    const apiResponse: ApiChatEventResponse = await response.json()

    // Check if the API call was successful
    // if (!apiResponse.uiSchema.success) {
    //   throw new Error(apiResponse.uiSchema.message || 'API call failed')
    // }

    // Transform API response to our internal format
    return {
      message: apiResponse.dataSchema.message,
      id: apiResponse.dataSchema.id,
      timestamp: apiResponse.dataSchema.timestamp,
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection.')
    }

    // Handle other errors
    throw new Error('An unexpected error occurred')
  }
}

interface GetChatSchemaParams {
  appKey?: string
  contentId?: string
  projectId?: number
  // filterSelections: Record<string, any>[]
  isPageBuilder?: boolean
}

const getChatSchema = async ({ appKey, contentId, projectId, isPageBuilder }: GetChatSchemaParams) => {
  if (!appKey || !contentId || !projectId) {
    throw new Error('Missing required parameters')
  }

  if (isPageBuilder || USE_MOCK_DATA) {
    return Promise.resolve(
      (await import('@/catalog-apps/mock-data/chat/chat-component-response.json')).json as ApiChatContentResponse
    )
  }

  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }
  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: contentId,
      project_id: projectId,
      filters: [],
    }),
  })
  const data = (await response.json()) as ApiChatContentResponse
  if (response.ok) {
    if (data.uiSchema.handlerId) {
      sessionStorage.setItem(CHAT_HANDLER_ID_STORAGE_KEY, data.uiSchema.handlerId)
    }
    return data
  }
  throw new Error('Failed to fetch form')
}

interface GetLiveChatSchemaParams {
  appKey?: string
  contentId?: string
  projectId?: number
  isPageBuilder?: boolean
}

/**
 * Fetch LiveChat UI schema and session data from backend.
 * Backend checks Redis for existing session based on user context.
 */
const getLiveChatSchema = async ({
  appKey,
  contentId,
  projectId,
  isPageBuilder,
}: GetLiveChatSchemaParams): Promise<ApiLiveChatContentResponse> => {
  if (!appKey || !contentId || !projectId) {
    throw new Error('Missing required parameters')
  }

  if (isPageBuilder) {
    // Return mock data for page builder
    return {
      uiSchema: {
        title: 'AI Live Chat',
        description: 'Live chat with AI assistant',
      },
      dataSchema: { success: false, error: 'Page builder mode' },
    }
  }

  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }

  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: contentId,
      project_id: projectId,
      filters: [],
    }),
  })

  const data = (await response.json()) as ApiLiveChatContentResponse

  if (response.ok) {
    return data
  }

  throw new Error('Failed to fetch live chat schema')
}

/**
 * Get existing session data from sessionStorage if available.
 */
const getExistingLiveChatSession = (contentId: string) => {
  return getLiveChatSession(contentId)
}

/**
 * Clear the live chat session.
 * Call this when the user explicitly wants to start a new chat session.
 */
const clearLiveChatSessionData = (): void => {
  clearLiveChatSession()
}

interface StartLiveChatSessionParams {
  appKey: string
  handlerId: string
  projectId: number
  contentId: string
  existingRequestId?: string
  existingTaskId?: number
}

/**
 * Start a new live chat session by calling the event handler.
 * This creates a new chat worker task and returns the connection details.
 */
const startLiveChatSession = async ({
  appKey,
  handlerId,
  projectId,
  contentId,
  existingRequestId,
  existingTaskId,
}: StartLiveChatSessionParams): Promise<LiveChatDataSchema> => {
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }

  const url = `${ENDPOINTS.CC_DOTNET}/event`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: handlerId,
      project_id: projectId,
      filters: [],
      payload:
        existingRequestId && existingTaskId
          ? {
              existingRequestId,
              existingTaskId,
            }
          : {},
    }),
  })

  const data = (await response.json()) as ApiLiveChatEventResponse

  if (response.ok && data.dataSchema.success) {
    // Save session data for future reconnections (only if we have the required fields)
    if (data.dataSchema.requestId !== undefined && data.dataSchema.taskId !== undefined) {
      saveLiveChatSession({
        requestId: data.dataSchema.requestId,
        taskId: data.dataSchema.taskId,
        appKey: data.dataSchema.appKey,
        contentId,
        createdAt: Date.now(),
      })
    }
    return data.dataSchema
  }

  throw new Error(data.dataSchema.error || 'Failed to start live chat session')
}

interface DisconnectLiveChatSessionParams {
  appKey: string
  projectId: number
}

/**
 * Disconnect from the current live chat session.
 * This calls the disconnect event handler to clean up the session.
 */
const disconnectLiveChatSession = async ({
  appKey,
  projectId,
}: DisconnectLiveChatSessionParams): Promise<{ success: boolean; message?: string }> => {
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }

  const url = `${ENDPOINTS.CC_DOTNET}/event`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: '/showcase/live_chat/disconnect',
      project_id: projectId,
      filters: [],
      payload: {},
    }),
  })

  const data = await response.json()

  if (response.ok && data.dataSchema?.success) {
    // Clear local session data
    clearLiveChatSession()
    return { success: true, message: data.uiSchema?.message }
  }

  return { success: false, message: data.dataSchema?.error || 'Failed to disconnect' }
}

interface SendLiveChatMessageParams {
  appKey: string
  projectId: number
  eventId: string
  message: string
  chatId: string
  filters?: Record<string, unknown>[]
}

/**
 * Send a message to the live chat via catalog event handler.
 *
 * This is the new message-based flow where:
 * 1. Frontend sends message to catalog event handler
 * 2. Event handler pushes message to queue
 * 3. Worker picks up message and responds via SignalR
 * 4. Frontend receives response via existing SignalR connection
 */
const sendLiveChatMessage = async ({
  appKey,
  projectId,
  eventId,
  message,
  chatId,
  filters = [],
}: SendLiveChatMessageParams): Promise<{ success: boolean; taskId?: number; error?: string }> => {
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }

  const url = `${ENDPOINTS.CC_DOTNET}/event`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: eventId,
      project_id: projectId,
      filters,
      payload: {
        message,
        chatId,
      },
    }),
  })

  const data = await response.json()

  if (response.ok && data.dataSchema?.success) {
    return { success: true, taskId: data.dataSchema.taskId }
  }

  return { success: false, error: data.dataSchema?.error || 'Failed to send message' }
}

export {
  fetchChatHistory,
  sendChatMessage,
  saveMessagesToStorage,
  loadMessagesFromStorage,
  getChatSchema,
  getLiveChatSchema,
  getExistingLiveChatSession,
  startLiveChatSession,
  clearLiveChatSessionData,
  disconnectLiveChatSession,
  sendLiveChatMessage,
}
